import { InfoItemHtml } from '@chiroup/components/InfoItemHtml';
import { prescription } from '@chiroup/core/functions/createPrescription';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { hcVerbiage } from '../../common/verbiage';
import { usePatientPortal } from '../../contexts/PatientPortalContext';
import FallbackImage from '../common/FallbackImage';
import PainLevelSurvey from './PainLevelSurvey';
import ResponsivePlayer from './ResponsivePlayer';

const ExerciseDetail: React.FC<{ basePath: string }> = ({ basePath }) => {
  const { selectedPatient, language } = usePatientPortal();
  const { exerciseId } = useParams();
  const navigate = useNavigate();
  const primaryColor = selectedPatient?.clinic?.primaryColor || '#77bd43';
  const [painSurveyOpen, setPainSurveyOpen] = useState(false);

  const exercise = useMemo(() => {
    return selectedPatient?.exercises?.find((e: any) => String(e.ID) === exerciseId);
  }, [exerciseId, selectedPatient?.exercises]);

  const subtitle = useMemo(() => {
    if (!exercise) return '';

    return prescription(exercise, language);
  }, [exercise, language]);

  const { previousExerciseID, nextExerciseID } = useMemo(() => {
    const exercisesForPhase = selectedPatient?.exercises?.filter(
      (e: any) => e.phase === exercise?.phase
    );
    const idx = exercisesForPhase?.findIndex((e: any) => String(e.ID) === exerciseId);

    return {
      previousExerciseID: exercisesForPhase?.[idx - 1]?.ID,
      nextExerciseID: exercisesForPhase?.[idx + 1]?.ID,
    };
  }, [exerciseId, selectedPatient?.exercises, exercise?.phase]);

  if (!exercise) {
    return (
      <div className="text-center py-16">
        <p className="text-sm text-gray-400 dark:text-gray-500">Exercise not found.</p>
        <Link
          to={`${basePath}/exercises`}
          className="mt-4 inline-flex text-sm font-medium"
          style={{ color: primaryColor }}
        >
          Back to exercises
        </Link>
      </div>
    );
  }

  const hasVideo = !!exercise.video;
  const hasImage = !!exercise.img;
  const hasNav = !!previousExerciseID || !!nextExerciseID;
  // "Mark exercises complete" only makes sense on the last
  // active-phase exercise. Phase 2 means "upcoming" — the patient
  // hasn't been prescribed those yet, so nothing to mark complete.
  // Backend overwrites today's existing entry on resubmit, so we
  // keep the CTA visible all day in case the patient wants to fix
  // a mis-tap.
  const showCompleteCta = exercise.phase === 1 && !nextExerciseID;

  const handleSurveyOpen = (next: boolean) => {
    setPainSurveyOpen(next);
    // Closing without a previous exercise means there's nowhere
    // useful to stay on this page, so bounce to the list. Mirrors
    // the legacy app's behavior.
    if (!next && !previousExerciseID) {
      navigate(`${basePath}/exercises`);
    }
  };

  // `useDivs` swaps the internal `<dt>/<dd>` markup for `<div>`s, killing
  // the browser's default `margin-inline-start: 40px` on `<dd>` that was
  // indenting the description text inside the card. Pre-existing in
  // InfoItemHtml for exactly this kind of use.
  const descriptionBody = (
    <InfoItemHtml useDivs className="normal-styling" value={exercise.descr} />
  );

  return (
    <div className="flex flex-col gap-5 max-w-3xl">
      <Link
        to={`${basePath}/exercises`}
        className="inline-flex items-center gap-1 text-sm text-gray-400 dark:text-gray-500 font-medium"
      >
        <ChevronLeftIcon className="h-4 w-4" />
        {hcVerbiage.exercises[language]}
      </Link>

      {(hasVideo || hasImage) && (
        <div className="relative -mx-4 sm:-mx-6">
          {hasVideo ? (
            <div className="overflow-hidden rounded-2xl mx-4 sm:mx-6">
              <ResponsivePlayer url={exercise.video as string} />
            </div>
          ) : (
            <div className="mx-4 sm:mx-6">
              <FallbackImage
                src={`${exercise.img}${exercise.img!.includes('?') ? '&' : '?'}w=800&q=85`}
                alt={exercise.name}
                imgClassName="w-full object-cover rounded-2xl"
              />
            </div>
          )}
        </div>
      )}

      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
          {exercise.name}
        </h2>
        {subtitle && (
          <span
            className="mt-2 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold"
            style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}
          >
            {subtitle}
          </span>
        )}
      </div>

      {!!exercise.descr && (
        <section className="portal-card p-4 flex flex-col gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
            Instructions
          </h3>
          <div className="prose prose-sm prose-gray dark:prose-invert max-w-none leading-relaxed text-pretty">
            {descriptionBody}
          </div>
        </section>
      )}

      {(hasNav || showCompleteCta) && (
        <div className="flex items-center justify-between gap-3 pt-2">
          <div>
            {!!previousExerciseID && (
              <Link
                to={`${basePath}/exercises/${previousExerciseID}`}
                className="inline-flex items-center gap-1 text-sm font-medium text-gray-500 dark:text-gray-400"
              >
                <ChevronLeftIcon className="h-3.5 w-3.5" /> Previous
              </Link>
            )}
          </div>
          <div>
            {!!nextExerciseID ? (
              <Link
                to={`${basePath}/exercises/${nextExerciseID}`}
                className="inline-flex items-center gap-1 text-sm font-semibold"
                style={{ color: primaryColor }}
              >
                Next <ChevronRightIcon className="h-3.5 w-3.5" />
              </Link>
            ) : showCompleteCta ? (
              <button
                type="button"
                onClick={() => handleSurveyOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-sm font-semibold text-white active:scale-[0.98] transition-transform"
                style={{ backgroundColor: primaryColor }}
              >
                {hcVerbiage.complete[language]}
                <CheckCircleIcon className="size-4" />
              </button>
            ) : null}
          </div>
        </div>
      )}

      <PainLevelSurvey
        open={painSurveyOpen}
        setOpen={handleSurveyOpen}
        language={language === 'es' ? 'es' : 'en'}
        primaryColor={primaryColor}
      />
    </div>
  );
};

export default ExerciseDetail;
